/**
 * Utility functions for handling firstName dropdown options
 */

import { useState } from 'react';
import { splitFullName } from './util';

interface ProcessedNameResult {
  firstName: string;
  lastName: string;
  fullName: string;
}

interface ProcessNameOptions {
  firstNamesList: string[];
  lastName: string;
  defaultFirstName?: string;
}


export interface FirstNameOption {
  value: string;
  label: string;
}

export interface PersonalNameChangeCallbackProps {
  firstName: string;
  lastName: string;
  fullName: string;
}

/**
 * Creates dropdown options from an array of first names
 * @param firstNames - Array of first name strings
 * @returns Array of dropdown options with value and label
 */
export const createFirstNameOptions = (firstNames: string[] = []): FirstNameOption[] => {
  return firstNames.map((name) => ({
    value: name,
    label: name,
  }));
};

export interface PersonalNameDropdownProps {
  firstNames: string[];
  fullName?: string;
  selectedFirstName: string;
  onFirstNameChange: (props: PersonalNameChangeCallbackProps) => void;
}

export const usePersonalNameDropdown = (props: PersonalNameDropdownProps) => {
  const { firstNames, selectedFirstName, onFirstNameChange, fullName } = props;
  const { lastName } = splitFullName(fullName);
  const [firstName, setFirstName] = useState<string>(selectedFirstName);

  const options = createFirstNameOptions(firstNames);

  const getFullName = (_firstName: string) => {
    return `${_firstName} ${lastName || ''}`.trim();
  };

  const handleChange = (e: React.ChangeEvent<HTMLSelectElement>) => {
    const _val = e.target.value;

    onFirstNameChange({ firstName: _val, lastName, fullName: getFullName(_val) });
    setFirstName(e.target.value);
  };

  return {
    options,
    value: firstName,
    onChange: handleChange,
    lastName,
    fullName: `${firstName} ${lastName}`,
  };
};

export const processName = (options: ProcessNameOptions): ProcessedNameResult => {
  const { firstNamesList, lastName, defaultFirstName } = options;

  let firstName: string;

  if (defaultFirstName && typeof defaultFirstName === 'string' && firstNamesList.includes(defaultFirstName)) {
    firstName = defaultFirstName;
  } else {
    firstName = firstNamesList.length > 0 ? firstNamesList[0] : '';
  }

  const fullName = `${firstName} ${lastName}`.trim();

  return {
    firstName: firstName.trim(),
    lastName: lastName.trim(),
    fullName,
  };
};
